import React from "react";
import { fromNow } from "@/lib/crm";
import { getNotificationMeta } from "@/lib/notifications";

/**
 * Colour-coded notification card. Left border accent + icon badge tinted per
 * notification kind. Unread notifications get a brighter background.
 *
 * `slide` prop attaches a subtle mount animation (used for newly-arrived items).
 */
export function NotificationCard({ n, onClick, slide = false, dense = false }) {
  const meta = getNotificationMeta(n);
  const Icon = meta.icon;
  const accent = meta.accent;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`notif-card-${n.id}`}
      data-notif-kind={n.kind || "info"}
      className={`group relative flex w-full gap-3 overflow-hidden rounded-lg border border-border bg-card text-left transition hover:-translate-y-[1px] hover:shadow-md ${
        n.read ? "opacity-90" : "bg-primary/5"
      } ${dense ? "p-3" : "p-4"} ${slide ? "animate-slide-in-top" : ""}`}
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      {/* Icon badge */}
      <div
        className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: `${accent}22`, color: accent }}
      >
        <Icon className="h-4 w-4" />
      </div>

      {/* Body */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div
              className="mono text-[10px] font-bold uppercase tracking-widest"
              style={{ color: accent }}
            >
              {meta.label}
            </div>
            <div className="mt-0.5 truncate font-semibold leading-tight">{n.title}</div>
          </div>
          <div className="mono shrink-0 text-[11px] text-muted-foreground">
            {fromNow(n.created_at)}
          </div>
        </div>
        <div className={`mt-1 text-sm text-muted-foreground ${dense ? "line-clamp-1" : "line-clamp-2"}`}>
          {n.body}
        </div>
      </div>

      {/* Unread pip */}
      {!n.read && (
        <span
          aria-hidden
          className="absolute right-3 top-3 h-2 w-2 rounded-full"
          style={{ backgroundColor: accent }}
        />
      )}
    </button>
  );
}
